import { safeRegisterTool } from "./registerTool";
import { dataResult, textResult } from "./toolResult";
import { navigate } from "gatsby";

/**
 * Tool WebMCP per le news (blog + rassegna stampa). I dati arrivano statici
 * (useStaticQuery in WebMcpTools) e vengono catturati alla registrazione: non
 * cambiano a runtime. I tag esistono solo sui blog post; le rassegne entrano
 * solo nella ricerca per parola chiave.
 */

const includesCI = (haystack, needle) =>
  String(haystack || "")
    .toLowerCase()
    .includes(String(needle || "").toLowerCase());

const formatData = (date) => {
  if (!date) return null;
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleDateString("it-IT", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

/**
 * Normalizza i dati statici in una lista unica di news.
 * @returns {{items: Array, tags: Array}}
 */
function buildNewsModel({ posts = [], rassegne = [], tags = [] }) {
  const blog = posts.map((p) => ({
    tipo: "blog",
    titolo: p.acfArticolo?.titoloUltimeNews || p.title,
    titoloOriginale: p.title,
    uri: p.uri,
    data: p.date,
    estratto: p.acfArticolo?.estratto || "",
    tags: (p.tags?.nodes || []).map((t) => ({ name: t.name, slug: t.slug })),
  }));

  const press = rassegne.map((r) => ({
    tipo: "rassegna",
    titolo: r.title,
    titoloOriginale: r.title,
    uri: r.uri,
    data: r.date,
    estratto: r.acfRassegna?.estratto || "",
    tags: [],
  }));

  const tagList = tags.map((t) => ({
    nome: t.name,
    slug: t.slug,
    uri: t.uri,
    conteggio: t.count || 0,
  }));

  return { items: [...blog, ...press], tags: tagList };
}

const toCompact = (item) => ({
  titolo: item.titolo,
  data: formatData(item.data),
  estratto: item.estratto,
  uri: item.uri,
  tipo: item.tipo,
  tag: item.tags.map((t) => t.name),
});

const matchTag = (item, tag) =>
  item.tags.some(
    (t) =>
      includesCI(t.name, tag) ||
      String(t.slug || "").toLowerCase() === String(tag).toLowerCase()
  );

/**
 * Registra i tool news. Ritorna una funzione di cleanup.
 */
export function registerNewsTools(modelContext, rawData) {
  if (!modelContext) return () => {};
  const { items, tags } = buildNewsModel(rawData || {});

  const cercaNews = (args = {}) => {
    const { query, tag, tipo = "tutti", limite = 5, naviga } = args;

    let results = items;
    if (tipo === "blog" || tipo === "rassegna") {
      results = results.filter((i) => i.tipo === tipo);
    }
    if (tag) {
      // i tag esistono solo sui blog: filtrare per tag esclude le rassegne
      results = results.filter((i) => matchTag(i, tag));
    }
    if (query) {
      results = results.filter(
        (i) => includesCI(i.titolo, query) || includesCI(i.estratto, query)
      );
    }

    const max = Math.min(Math.max(parseInt(limite, 10) || 5, 1), 20);
    const limited = results.slice(0, max);

    if (naviga === "tag") {
      if (!tag) {
        return textResult(
          "Per navigare al tag indica il parametro 'tag'.",
          { isError: true }
        );
      }
      const tagNode = tags.find(
        (t) => includesCI(t.nome, tag) || t.slug?.toLowerCase() === String(tag).toLowerCase()
      );
      if (!tagNode?.uri) {
        return textResult(`Tag "${tag}" non trovato.`, { isError: true });
      }
      navigate(tagNode.uri);
      return dataResult(`Apro la pagina del tag "${tagNode.nome}".`, {
        tag: tagNode,
        risultati: limited.map(toCompact),
      });
    }

    if (naviga === "articolo") {
      if (limited.length === 0) {
        return textResult("Nessuna news corrisponde ai criteri.", {
          isError: true,
        });
      }
      navigate(limited[0].uri);
      return dataResult(`Apro: ${limited[0].titolo}.`, {
        risultati: limited.map(toCompact),
      });
    }

    if (limited.length === 0) {
      return textResult("Nessuna news corrisponde ai criteri.");
    }
    return dataResult(
      `Trovate ${results.length} news${
        results.length > limited.length ? ` (mostro le prime ${max})` : ""
      }.`,
      { totale: results.length, risultati: limited.map(toCompact) }
    );
  };

  const listaTag = (args = {}) => {
    const { filtro } = args;
    let list = tags.filter((t) => t.conteggio > 0 || t.uri);
    if (filtro) {
      list = list.filter((t) => includesCI(t.nome, filtro));
    }
    list = [...list].sort((a, b) => b.conteggio - a.conteggio);
    return dataResult(`${list.length} tag disponibili.`, { tag: list });
  };

  const unregisters = [
    safeRegisterTool(modelContext, {
      name: "lista_tag_news",
      description:
        "Elenca i tag disponibili per le news del blog (con conteggio articoli e URL della pagina-archivio). Usalo per scoprire i tag validi prima di chiamare cerca_news.",
      inputSchema: {
        type: "object",
        properties: {
          filtro: {
            type: "string",
            description: "Sottostringa opzionale per filtrare i nomi dei tag.",
          },
        },
      },
      execute: (args) => listaTag(args || {}),
    }),
    safeRegisterTool(modelContext, {
      name: "cerca_news",
      description:
        "Cerca tra le news del sito (articoli del blog e rassegna stampa) per parola chiave e/o tag. Puo` anche aprire il primo articolo o la pagina-archivio del tag. I tag valgono solo per gli articoli del blog (usa prima lista_tag_news).",
      inputSchema: {
        type: "object",
        properties: {
          query: {
            type: "string",
            description: "Parola chiave su titolo/estratto.",
          },
          tag: {
            type: "string",
            description:
              "Nome o slug di un tag (solo blog). Vedi lista_tag_news.",
          },
          tipo: {
            type: "string",
            enum: ["tutti", "blog", "rassegna"],
            description: "Tipo di contenuto da cercare. Default: tutti.",
          },
          limite: {
            type: "number",
            description: "Numero massimo di risultati (1-20). Default 5.",
          },
          naviga: {
            type: "string",
            enum: ["articolo", "tag"],
            description:
              "Se 'articolo' apre il primo risultato; se 'tag' apre la pagina-archivio del tag.",
          },
        },
      },
      execute: (args) => cercaNews(args || {}),
    }),
  ];

  return () => unregisters.forEach((u) => typeof u === "function" && u());
}
